import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, CreditCard, Link2, ArrowLeft, Check, X, Mail, Phone, Globe, Save, ExternalLink, Download, Smartphone, Lock, Key, Palette, Sun, Moon, Clock, BellRing, Target, TrendingUp, Zap, AlertTriangle, Building2, DollarSign } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';
import LinkGoogleAdsAccount from './LinkGoogleAdsAccount';
import UserMenu from './UserMenu';
import { useToast } from './Toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function Settings() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'accounts');
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { theme, themePreference, setTheme } = useTheme();
  const { showError } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // State for settings
  const [userInfo, setUserInfo] = useState(null);
  const [isPartner, setIsPartner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({
    emailReports: true,
    performanceAlerts: true,
    budgetAlerts: true,
    weeklyDigest: true,
    productUpdates: false,
    marketingEmails: false
  });
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Optimization settings state
  const [optimizationSettings, setOptimizationSettings] = useState({
    defaultTargetCpa: '',
    defaultTargetRoas: '',
    industry: '',
    autoApplyEnabled: false,
    autoApplyThreshold: 0.85,
    maxBidIncreasePercent: 25,
    maxBidDecreasePercent: 40,
    lookbackDays: 30,
    qualityScoreAlertThreshold: '',
    pauseKeywordClicksThreshold: '',
    negativeSearchTermClicksThreshold: ''
  });
  const [industries, setIndustries] = useState([]);
  const [resolvedTargets, setResolvedTargets] = useState(null);
  const [optimizationSaveStatus, setOptimizationSaveStatus] = useState(null);
  const [loadingOptimization, setLoadingOptimization] = useState(false);
  
  // MCC sub-accounts for per-account settings
  const [subAccounts, setSubAccounts] = useState([]);
  const [selectedOptAccountId, setSelectedOptAccountId] = useState('all'); // 'all' = global, or specific account ID

  const tabs = [
    { id: 'accounts', label: 'Connected Accounts', icon: Link2 },
    { id: 'optimization', label: 'Optimization', icon: TrendingUp },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  useEffect(() => {
    fetchUserInfo();
    fetchPartnerStatus();
    fetchSubAccounts();
    fetchOptimizationSettings(selectedOptAccountId);
    fetchIndustries();
  }, []);
  
  // Reload settings when selected account changes
  useEffect(() => {
    if (selectedOptAccountId) {
      fetchOptimizationSettings(selectedOptAccountId);
    }
  }, [selectedOptAccountId]);
  
  const fetchSubAccounts = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/user/accounts?all=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.accounts) {
        // Map to consistent format (user accounts use customerId, not id)
        const mapped = data.accounts.map(acc => ({
          id: acc.customerId || acc.id,
          name: acc.accountName || acc.name || acc.descriptive_name,
          currency: acc.currency,
          timezone: acc.timezone
        }));
        setSubAccounts(mapped);
      }
    } catch (error) {
      console.error('Error fetching sub-accounts:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUserInfo(data);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Partners are billed per client via the Partner Dashboard, so the
  // self-serve billing tab does not apply to them.
  const fetchPartnerStatus = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/partner/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.isPartner) setIsPartner(true);
    } catch (error) {
      // Non-fatal — default to non-partner (self-serve billing shown).
    }
  };

  const fetchOptimizationSettings = async (accountId = 'all') => {
    setLoadingOptimization(true);
    try {
      const token = await getToken();
      const accountParam = accountId && accountId !== 'all' ? `?accountId=${accountId}` : '';
      
      const [settingsRes, targetsRes] = await Promise.all([
        fetch(`${API_URL}/api/optimization/settings${accountParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/optimization/resolved-targets${accountParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const settingsData = await settingsRes.json();
      const targetsData = await targetsRes.json();
      
      if (settingsData.success && settingsData.settings) {
        setOptimizationSettings({
          defaultTargetCpa: settingsData.settings.defaultTargetCpa || '',
          defaultTargetRoas: settingsData.settings.defaultTargetRoas || '',
          industry: settingsData.settings.industry || '',
          autoApplyEnabled: settingsData.settings.autoApplyEnabled || false,
          autoApplyThreshold: settingsData.settings.autoApplyThreshold || 0.85,
          maxBidIncreasePercent: settingsData.settings.maxBidIncreasePercent || 25,
          maxBidDecreasePercent: settingsData.settings.maxBidDecreasePercent || 40,
          lookbackDays: settingsData.settings.lookbackDays || 30,
          qualityScoreAlertThreshold: settingsData.settings.qualityScoreAlertThreshold || '',
          pauseKeywordClicksThreshold: settingsData.settings.pauseKeywordClicksThreshold || '',
          negativeSearchTermClicksThreshold: settingsData.settings.negativeSearchTermClicksThreshold || ''
        });
      } else {
        // Reset to defaults if no settings for this account
        setOptimizationSettings({
          defaultTargetCpa: '',
          defaultTargetRoas: '',
          industry: '',
          autoApplyEnabled: false,
          autoApplyThreshold: 0.85,
          maxBidIncreasePercent: 25,
          maxBidDecreasePercent: 40,
          lookbackDays: 30,
          qualityScoreAlertThreshold: '',
          pauseKeywordClicksThreshold: '',
          negativeSearchTermClicksThreshold: ''
        });
      }
      
      if (targetsData.success) {
        setResolvedTargets(targetsData.targets);
      }
    } catch (error) {
      console.error('Error fetching optimization settings:', error);
    } finally {
      setLoadingOptimization(false);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/optimization/industries`);
      const data = await response.json();
      if (data.success) {
        setIndustries(data.industries);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const saveOptimizationSettings = async () => {
    setOptimizationSaveStatus('saving');
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/optimization/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: selectedOptAccountId !== 'all' ? selectedOptAccountId : null,
          settings: {
            default_target_cpa: optimizationSettings.defaultTargetCpa ? parseFloat(optimizationSettings.defaultTargetCpa) : null,
            default_target_roas: optimizationSettings.defaultTargetRoas ? parseFloat(optimizationSettings.defaultTargetRoas) : null,
            industry: optimizationSettings.industry || null,
            auto_apply_enabled: optimizationSettings.autoApplyEnabled,
            auto_apply_threshold: optimizationSettings.autoApplyThreshold,
            max_bid_increase_percent: optimizationSettings.maxBidIncreasePercent,
            max_bid_decrease_percent: optimizationSettings.maxBidDecreasePercent,
            lookback_days: optimizationSettings.lookbackDays,
            quality_score_alert_threshold: optimizationSettings.qualityScoreAlertThreshold ? parseInt(optimizationSettings.qualityScoreAlertThreshold) : null,
            pause_keyword_clicks_threshold: optimizationSettings.pauseKeywordClicksThreshold ? parseInt(optimizationSettings.pauseKeywordClicksThreshold) : null,
            negative_search_term_clicks_threshold: optimizationSettings.negativeSearchTermClicksThreshold ? parseInt(optimizationSettings.negativeSearchTermClicksThreshold) : null
          }
        })
      });
      
      if (!response.ok) {
        console.error('Save settings response not ok:', response.status);
        setOptimizationSaveStatus('error');
        showError(`Failed to save settings (HTTP ${response.status})`);
        setTimeout(() => setOptimizationSaveStatus(null), 3000);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setOptimizationSaveStatus('saved');
        // Refresh resolved targets to show new source
        fetchOptimizationSettings(selectedOptAccountId);
        setTimeout(() => setOptimizationSaveStatus(null), 3000);
      } else {
        console.error('Save settings failed:', data.error);
        setOptimizationSaveStatus('error');
        showError(data.error || 'Failed to save optimization settings');
        setTimeout(() => setOptimizationSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error saving optimization settings:', error);
      setOptimizationSaveStatus('error');
      showError(error.message || 'Failed to save optimization settings');
      setTimeout(() => setOptimizationSaveStatus(null), 3000);
    }
  };

  const handleNotificationChange = async (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    
    setSaveStatus('saving');
    // Simulate API call
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }, 500);
  };

  const handleManageSubscription = () => {
    navigate('/subscription');
  };

  const handleBillingPortal = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/billing/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      showError('Unable to open billing portal. Please try again.');
    }
  };

  // Theme-aware styles
  const cardStyle = {
    background: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: '16px',
    padding: '32px'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: theme.colors.bg.tertiary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: '8px',
    color: theme.colors.text.secondary,
    fontSize: '0.95rem'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bg.primary,
      color: theme.colors.text.primary
    }}>
      {/* Top Bar with User Menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        background: theme.colors.bg.secondary,
        borderBottom: `1px solid ${theme.colors.border.primary}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: theme.colors.bg.tertiary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: '8px',
              color: theme.colors.text.primary,
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.bg.tertiary}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Notifications */}
          <button style={{
            position: 'relative',
            background: 'none',
            border: 'none',
            color: theme.colors.text.secondary,
            cursor: 'pointer',
            padding: '8px'
          }}>
            <BellRing size={20} />
            <span style={{
              position: 'absolute',
              top: 4,
              right: 4,
              background: '#ef4444',
              color: 'white',
              fontSize: '10px',
              fontWeight: '700',
              borderRadius: '50%',
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              3
            </span>
          </button>
          
          {/* User Menu */}
          <UserMenu />
        </div>
      </div>
      
      {/* Main Content with padding for fixed header */}
      <div style={{ padding: isMobile ? '70px 16px 24px 16px' : '80px 40px 40px 40px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: isMobile ? '24px' : '40px' }}>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <SettingsIcon size={isMobile ? 24 : 32} />
            Settings
          </h1>
          <p style={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            color: theme.colors.text.secondary,
            margin: 0
          }}>
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: isMobile ? '20px' : '32px',
          borderBottom: `1px solid ${theme.colors.border.primary}`,
          paddingBottom: '0',
          flexWrap: 'wrap',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 20px',
                  background: isActive ? theme.colors.bg.hover : 'transparent',
                  color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
                  border: 'none',
                  borderBottom: isActive ? `2px solid ${theme.colors.brand.primary}` : '2px solid transparent',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = theme.colors.bg.hover;
                    e.currentTarget.style.color = theme.colors.text.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.colors.text.secondary;
                  }
                }}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'accounts' && <LinkGoogleAdsAccount />}
          
          {/* OPTIMIZATION TAB */}
          {activeTab === 'optimization' && (
            <div style={{ maxWidth: '900px' }}>
              <div style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '16px',
                padding: '32px',
                marginBottom: '24px'
              }}>
                {/* Account Selector for MCC */}
                {subAccounts.length > 1 && (
                  <div style={{
                    padding: '16px 20px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: '12px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#6366f1', marginBottom: '4px' }}>
                        MCC Account Detected
                      </div>
                      <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
                        Set different targets per sub-account, or use global settings for all
                      </div>
                    </div>
                    <select
                      value={selectedOptAccountId}
                      onChange={(e) => setSelectedOptAccountId(e.target.value)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.border.primary}`,
                        background: theme.colors.bg.primary,
                        color: theme.colors.text.primary,
                        fontSize: '14px',
                        minWidth: '250px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="all">All Accounts (Global Settings)</option>
                      {subAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name || acc.descriptive_name} ({acc.id})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '12px', color: theme.colors.text.primary }}>
                      <Target size={24} style={{ color: '#6366f1' }} />
                      Optimization Targets
                      {selectedOptAccountId !== 'all' && (
                        <span style={{ 
                          fontSize: '0.8rem', 
                          background: '#6366f1',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontWeight: '500'
                        }}>
                          {subAccounts.find(a => a.id === selectedOptAccountId)?.name || selectedOptAccountId}
                        </span>
                      )}
                    </h2>
                    <p style={{ color: theme.colors.text.secondary, fontSize: '0.9rem', marginTop: '8px' }}>
                      {selectedOptAccountId === 'all' 
                        ? 'Configure default optimization goals for all accounts'
                        : 'Configure specific targets for this account (overrides global settings)'
                      }
                    </p>
                  </div>
                  {optimizationSaveStatus && (
                    <span style={{
                      padding: '8px 16px',
                      background: optimizationSaveStatus === 'saved' ? 'rgba(16, 185, 129, 0.2)' : 
                                 optimizationSaveStatus === 'error' ? 'rgba(239, 68, 68, 0.2)' :
                                 'rgba(99, 102, 241, 0.2)',
                      color: optimizationSaveStatus === 'saved' ? '#10b981' : 
                             optimizationSaveStatus === 'error' ? '#ef4444' : '#6366f1',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      {optimizationSaveStatus === 'saving' ? 'Saving...' : 
                       optimizationSaveStatus === 'saved' ? '✓ Saved!' : 
                       '✗ Error'}
                    </span>
                  )}
                </div>

                {/* Current Target Source Info */}
                {resolvedTargets && (
                  <div style={{
                    padding: '16px 20px',
                    background: resolvedTargets.isDefault 
                      ? 'rgba(245, 158, 11, 0.1)' 
                      : 'rgba(16, 185, 129, 0.1)',
                    border: `1px solid ${resolvedTargets.isDefault 
                      ? 'rgba(245, 158, 11, 0.3)' 
                      : 'rgba(16, 185, 129, 0.3)'}`,
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {resolvedTargets.isDefault ? (
                        <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                      ) : (
                        <Check size={20} style={{ color: '#10b981' }} />
                      )}
                      <div>
                        <div style={{ 
                          fontSize: '0.95rem', 
                          fontWeight: '600', 
                          color: resolvedTargets.isDefault ? '#f59e0b' : '#10b981',
                          marginBottom: '4px'
                        }}>
                          {resolvedTargets.isDefault 
                            ? 'Using Default Targets' 
                            : 'Custom Targets Active'}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                          Current: CPA €{resolvedTargets.cpa?.toFixed(2) || 'N/A'} | ROAS {resolvedTargets.roas?.toFixed(1) || 'N/A'}x
                          <span style={{ marginLeft: '8px', color: theme.colors.text.tertiary }}>
                            (Source: {resolvedTargets.source?.replace('_', ' ')})
                          </span>
                        </div>
                        {resolvedTargets.warnings?.length > 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#f59e0b', marginTop: '8px' }}>
                            💡 {resolvedTargets.warnings[0]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Target CPA & ROAS */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      color: theme.colors.text.primary 
                    }}>
                      <DollarSign size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                      Target CPA (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 50.00"
                      value={optimizationSettings.defaultTargetCpa}
                      onChange={(e) => setOptimizationSettings(prev => ({ ...prev, defaultTargetCpa: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: '8px',
                        color: theme.colors.text.primary,
                        fontSize: '1rem'
                      }}
                    />
                    <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                      Target Cost Per Acquisition for your campaigns
                    </p>
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      color: theme.colors.text.primary 
                    }}>
                      <TrendingUp size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                      Target ROAS
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g., 4.0"
                      value={optimizationSettings.defaultTargetRoas}
                      onChange={(e) => setOptimizationSettings(prev => ({ ...prev, defaultTargetRoas: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: '8px',
                        color: theme.colors.text.primary,
                        fontSize: '1rem'
                      }}
                    />
                    <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                      Target Return On Ad Spend (e.g., 4.0 = 400%)
                    </p>
                  </div>
                </div>

                {/* Industry Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: theme.colors.text.primary 
                  }}>
                    <Building2 size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                    Industry (for benchmark defaults)
                  </label>
                  <select
                    value={optimizationSettings.industry}
                    onChange={(e) => setOptimizationSettings(prev => ({ ...prev, industry: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: theme.colors.bg.tertiary,
                      border: `1px solid ${theme.colors.border.primary}`,
                      borderRadius: '8px',
                      color: theme.colors.text.primary,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select your industry...</option>
                    {industries.map(ind => (
                      <option key={ind.id} value={ind.id}>
                        {ind.displayName} (avg CPA: €{ind.avgTargetCpa?.toFixed(0) || 'N/A'})
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                    Used for smart defaults when no target is configured
                  </p>
                </div>

                {/* Client-specific recommendation rules */}
                <div style={{
                  padding: '20px',
                  background: theme.colors.bg.tertiary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: '12px',
                  marginBottom: '32px'
                }}>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    margin: '0 0 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: theme.colors.text.primary
                  }}>
                    <AlertTriangle size={18} style={{ color: '#f59e0b' }} />
                    Client-specific optimization rules
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: '0 0 20px 0' }}>
                    These thresholds only affect recommendations and alerts. Leave a field empty to use the Bidoryx default.
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: theme.colors.text.primary
                      }}>
                        Quality Score alert below
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={optimizationSettings.qualityScoreAlertThreshold}
                        placeholder="Default: 5"
                        onChange={(e) => setOptimizationSettings(prev => ({
                          ...prev,
                          qualityScoreAlertThreshold: e.target.value
                        }))}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: theme.colors.bg.secondary,
                          border: `1px solid ${theme.colors.border.primary}`,
                          borderRadius: '8px',
                          color: theme.colors.text.primary,
                          fontSize: '1rem'
                        }}
                      />
                      <p style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                        Default: below 5. Example: 5 means QS 1-4 gets flagged.
                      </p>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: theme.colors.text.primary
                      }}>
                        Pause keyword after clicks
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={optimizationSettings.pauseKeywordClicksThreshold}
                        placeholder="Default: by account size"
                        onChange={(e) => setOptimizationSettings(prev => ({
                          ...prev,
                          pauseKeywordClicksThreshold: e.target.value
                        }))}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: theme.colors.bg.secondary,
                          border: `1px solid ${theme.colors.border.primary}`,
                          borderRadius: '8px',
                          color: theme.colors.text.primary,
                          fontSize: '1rem'
                        }}
                      />
                      <p style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                        Applies when conversions are 0. Default scales with account size.
                      </p>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        marginBottom: '8px',
                        color: theme.colors.text.primary
                      }}>
                        Negative search term after clicks
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={optimizationSettings.negativeSearchTermClicksThreshold}
                        placeholder="Default: >10"
                        onChange={(e) => setOptimizationSettings(prev => ({
                          ...prev,
                          negativeSearchTermClicksThreshold: e.target.value
                        }))}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: theme.colors.bg.secondary,
                          border: `1px solid ${theme.colors.border.primary}`,
                          borderRadius: '8px',
                          color: theme.colors.text.primary,
                          fontSize: '1rem'
                        }}
                      />
                      <p style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                        Applies when conversions are 0. Default keeps the existing >10 clicks rule.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <button
                  onClick={saveOptimizationSettings}
                  disabled={optimizationSaveStatus === 'saving'}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: optimizationSaveStatus === 'saving' ? 'not-allowed' : 'pointer',
                    opacity: optimizationSaveStatus === 'saving' ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Save size={18} />
                  {optimizationSaveStatus === 'saving' ? 'Saving...' : 'Save Optimization Settings'}
                </button>
              </div>

              {/* Bid Limits Section */}
              <div style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '16px',
                padding: '32px',
                marginBottom: '24px'
              }}>
                <h3 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '600', 
                  marginBottom: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  color: theme.colors.text.primary 
                }}>
                  <Zap size={20} style={{ color: '#f59e0b' }} />
                  Bid Adjustment Limits
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      color: theme.colors.text.primary 
                    }}>
                      Max Bid Increase (%)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={optimizationSettings.maxBidIncreasePercent}
                      onChange={(e) => setOptimizationSettings(prev => ({ 
                        ...prev, 
                        maxBidIncreasePercent: parseInt(e.target.value) || 25 
                      }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: '8px',
                        color: theme.colors.text.primary,
                        fontSize: '1rem'
                      }}
                    />
                    <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                      Maximum percentage to increase bids per recommendation
                    </p>
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      marginBottom: '8px', 
                      color: theme.colors.text.primary 
                    }}>
                      Max Bid Decrease (%)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={optimizationSettings.maxBidDecreasePercent}
                      onChange={(e) => setOptimizationSettings(prev => ({ 
                        ...prev, 
                        maxBidDecreasePercent: parseInt(e.target.value) || 40 
                      }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: '8px',
                        color: theme.colors.text.primary,
                        fontSize: '1rem'
                      }}
                    />
                    <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                      Maximum percentage to decrease bids per recommendation
                    </p>
                  </div>
                </div>
              </div>

              {/* Analysis Settings */}
              <div style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '16px',
                padding: '32px'
              }}>
                <h3 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '600', 
                  marginBottom: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  color: theme.colors.text.primary 
                }}>
                  <SettingsIcon size={20} style={{ color: '#6366f1' }} />
                  Analysis Settings
                </h3>
                
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '600', 
                    marginBottom: '8px', 
                    color: theme.colors.text.primary 
                  }}>
                    Lookback Period (days)
                  </label>
                  <select
                    value={optimizationSettings.lookbackDays}
                    onChange={(e) => setOptimizationSettings(prev => ({ 
                      ...prev, 
                      lookbackDays: parseInt(e.target.value) 
                    }))}
                    style={{
                      width: '200px',
                      padding: '12px 16px',
                      background: theme.colors.bg.tertiary,
                      border: `1px solid ${theme.colors.border.primary}`,
                      borderRadius: '8px',
                      color: theme.colors.text.primary,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <p style={{ fontSize: '0.8rem', color: theme.colors.text.tertiary, marginTop: '6px' }}>
                    Time period used for performance analysis and recommendations
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderRadius: '16px',
                padding: '32px'
              }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <User size={24} />
                  Profile Information
                </h2>

                {/* Profile Photo */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', color: theme.colors.text.primary }}>
                    Profile Photo
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: user?.imageUrl ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      fontWeight: '700',
                      color: 'white',
                      overflow: 'hidden'
                    }}>
                      {user?.imageUrl ? (
                        <img src={user.imageUrl} alt={user.fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        user?.fullName?.charAt(0) || 'U'
                      )}
                    </div>
                    <div>
                      <p style={{ fontSize: '0.9rem', color: theme.colors.text.secondary, marginBottom: '8px' }}>
                        Profile photo managed by Clerk
                      </p>
                      <a
                        href="https://dashboard.clerk.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#6366f1',
                          fontSize: '0.85rem',
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        Manage in Clerk <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: theme.colors.text.primary }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={user?.fullName || ''}
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.95rem'
                    }}
                  />
                </div>

                {/* Email */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: theme.colors.text.primary }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.primaryEmailAddress?.emailAddress || ''}
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.95rem'
                    }}
                  />
                </div>

                {/* Subscription Info */}
                {userInfo && (
                  <div style={{
                    marginTop: '32px',
                    padding: '20px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: '#a5b4fc' }}>
                      Current Plan
                    </h3>
                    <p style={{ fontSize: '0.95rem', color: theme.colors.text.secondary, marginBottom: '12px' }}>
                      You're on the <strong style={{ color: theme.colors.text.primary }}>{userInfo.subscription.tierName}</strong> plan
                    </p>
                    <button
                      onClick={handleManageSubscription}
                      style={{
                        padding: '10px 20px',
                        background: '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#5558e3'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#6366f1'}
                    >
                      Change Plan
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* APPEARANCE TAB */}
          {activeTab === 'appearance' && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Palette size={24} />
                  Appearance
                </h2>

                {/* Theme Selection */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: 'rgba(255,255,255,0.9)' }}>
                    Theme
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
                    Choose your preferred color scheme for Bidoryx
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    {/* Dark Mode */}
                    <button
                      onClick={() => setTheme('dark')}
                      style={{
                        padding: '24px',
                        background: themePreference === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.03)',
                        border: themePreference === 'dark' ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        if (themePreference !== 'dark') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (themePreference !== 'dark') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}>
                          <Moon size={20} />
                        </div>
                        {themePreference === 'dark' && (
                          <Check size={20} style={{ color: '#8b5cf6', marginLeft: 'auto' }} />
                        )}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px' }}>
                        Dark
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Easy on the eyes, perfect for long sessions
                      </div>
                    </button>

                    {/* Light Mode */}
                    <button
                      onClick={() => setTheme('light')}
                      style={{
                        padding: '24px',
                        background: themePreference === 'light' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.03)',
                        border: themePreference === 'light' ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        if (themePreference !== 'light') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (themePreference !== 'light') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1f2937',
                          border: '1px solid #e5e7eb'
                        }}>
                          <Sun size={20} />
                        </div>
                        {themePreference === 'light' && (
                          <Check size={20} style={{ color: '#8b5cf6', marginLeft: 'auto' }} />
                        )}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px' }}>
                        Light
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Clean and bright, great for daytime work
                      </div>
                    </button>

                    {/* Auto Mode */}
                    <button
                      onClick={() => setTheme('auto')}
                      style={{
                        padding: '24px',
                        background: themePreference === 'auto' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.03)',
                        border: themePreference === 'auto' ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        if (themePreference !== 'auto') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (themePreference !== 'auto') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, #ffffff 0%, #1a1a2e 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}>
                          <Clock size={20} />
                        </div>
                        {themePreference === 'auto' && (
                          <Check size={20} style={{ color: '#8b5cf6', marginLeft: 'auto' }} />
                        )}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px' }}>
                        Auto
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Switches automatically based on time of day
                      </div>
                    </button>
                  </div>
                </div>

                {/* Auto Mode Info */}
                {themePreference === 'auto' && (
                  <div style={{
                    padding: '20px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    marginBottom: '32px'
                  }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Clock size={18} />
                      Auto Mode Schedule
                    </h3>
                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
                      🌙 <strong>Dark mode:</strong> 6:00 PM - 8:00 AM<br />
                      ☀️ <strong>Light mode:</strong> 8:00 AM - 6:00 PM<br />
                      <br />
                      Currently using: <strong style={{ color: theme.mode === 'dark' ? '#8b5cf6' : '#f59e0b' }}>
                        {theme.mode === 'dark' ? 'Dark' : 'Light'} theme
                      </strong>
                    </p>
                  </div>
                )}

                {/* Theme Preview */}
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: 'rgba(255,255,255,0.9)' }}>
                    Preview
                  </h3>
                  <div style={{
                    padding: '24px',
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: '12px'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '8px' }}>
                        Sample Dashboard Card
                      </div>
                      <div style={{ fontSize: '0.9rem', color: theme.colors.text.secondary }}>
                        This is how your interface will look with the selected theme
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                      <div style={{
                        flex: 1,
                        padding: '16px',
                        background: theme.colors.bg.tertiary,
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.border.primary}`
                      }}>
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.tertiary, marginBottom: '6px' }}>
                          Clicks
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: theme.colors.text.primary }}>
                          1,234
                        </div>
                      </div>
                      <div style={{
                        flex: 1,
                        padding: '16px',
                        background: theme.colors.bg.tertiary,
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.border.primary}`
                      }}>
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.tertiary, marginBottom: '6px' }}>
                          Cost
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: theme.colors.text.primary }}>
                          €567
                        </div>
                      </div>
                    </div>
                    <button
                      style={{
                        padding: '12px 24px',
                        background: theme.colors.brand.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Sample Button
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Bell size={24} />
                    Notification Preferences
                  </h2>
                  {saveStatus && (
                    <span style={{
                      padding: '6px 12px',
                      background: saveStatus === 'saved' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(99, 102, 241, 0.2)',
                      color: saveStatus === 'saved' ? '#10b981' : '#6366f1',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {saveStatus === 'saving' ? 'Saving...' : '✓ Saved'}
                    </span>
                  )}
                </div>

                {/* Email Notifications */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: theme.colors.text.primary }}>
                    Email Notifications
                  </h3>
                  {[
                    { key: 'emailReports', label: 'Daily Performance Reports', desc: 'Receive daily summaries of your campaign performance' },
                    { key: 'performanceAlerts', label: 'Performance Alerts', desc: 'Get notified when campaigns exceed or underperform targets' },
                    { key: 'budgetAlerts', label: 'Budget Alerts', desc: 'Alerts when campaigns are close to their budget limits' },
                    { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Weekly overview of all account activity' },
                  ].map(item => (
                    <div key={item.key} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                          {item.desc}
                        </div>
                      </div>
                      <button
                        onClick={() => handleNotificationChange(item.key)}
                        style={{
                          width: '48px',
                          height: '28px',
                          borderRadius: '14px',
                          background: notifications[item.key] ? '#10b981' : 'rgba(255,255,255,0.2)',
                          border: 'none',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      >
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '4px',
                          left: notifications[item.key] ? '24px' : '4px',
                          transition: 'all 0.3s'
                        }} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Marketing Communications */}
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: theme.colors.text.primary }}>
                    Marketing Communications
                  </h3>
                  {[
                    { key: 'productUpdates', label: 'Product Updates', desc: 'News about new features and improvements' },
                    { key: 'marketingEmails', label: 'Tips & Best Practices', desc: 'Educational content and optimization tips' },
                  ].map(item => (
                    <div key={item.key} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                          {item.desc}
                        </div>
                      </div>
                      <button
                        onClick={() => handleNotificationChange(item.key)}
                        style={{
                          width: '48px',
                          height: '28px',
                          borderRadius: '14px',
                          background: notifications[item.key] ? '#10b981' : 'rgba(255,255,255,0.2)',
                          border: 'none',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      >
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: 'white',
                          position: 'absolute',
                          top: '4px',
                          left: notifications[item.key] ? '24px' : '4px',
                          transition: 'all 0.3s'
                        }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* BILLING TAB */}
          {activeTab === 'billing' && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CreditCard size={24} />
                  Billing & Subscription
                </h2>

                {/* Partners are billed per client via the Partner Dashboard —
                    the self-serve billing details below don't apply to them. */}
                {isPartner && (
                  <div style={{
                    padding: '24px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <Building2 size={22} style={{ color: '#10b981' }} />
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
                        Partner billing
                      </h3>
                    </div>
                    <p style={{ fontSize: '0.95rem', color: theme.colors.text.secondary, lineHeight: 1.6, marginBottom: '18px' }}>
                      Je account wordt per klant afgerekend via het portfolio-model. Beheer je client-accounts,
                      facturatie en abonnement in het Partner Dashboard.
                    </p>
                    <button
                      onClick={() => navigate('/partner-dashboard')}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        padding: '12px 20px', borderRadius: '10px',
                        background: '#10b981', color: '#fff', border: 'none',
                        cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600
                      }}
                    >
                      Open Partner Dashboard <ExternalLink size={16} />
                    </button>
                  </div>
                )}

                {/* Current Plan */}
                {!isPartner && userInfo && (
                  <div style={{
                    padding: '24px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    marginBottom: '32px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                      <div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: 'white', marginBottom: '8px' }}>
                          {userInfo.subscription.tierName} Plan
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                          Status: <span style={{ color: '#10b981', fontWeight: '600' }}>Active</span>
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: 'white' }}>
                          {userInfo.subscription.tier === 'trial' ? '€0' : 
                           userInfo.subscription.tier === 'starter' ? '€99' :
                           userInfo.subscription.tier === 'growth' ? '€249' :
                           userInfo.subscription.tier === 'scale' ? '€399' :
                           userInfo.subscription.tier === 'enterprise' ? '€799' : '€0'}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                          per month
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                      <button
                        onClick={handleManageSubscription}
                        style={{
                          flex: 1,
                          padding: '12px 20px',
                          background: '#6366f1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#5558e3'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#6366f1'}
                      >
                        Change Plan
                      </button>
                      <button
                        onClick={handleBillingPortal}
                        style={{
                          flex: 1,
                          padding: '12px 20px',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                      >
                        Manage Billing
                      </button>
                    </div>
                  </div>
                )}

                {/* Usage This Month */}
                {!isPartner && userInfo && (
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: 'rgba(255,255,255,0.9)' }}>
                      Usage This Month
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      <div style={{
                        padding: '20px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                          Accounts Connected
                        </div>
                        <div style={{ fontSize: '1.8rem', fontWeight: '700', color: 'white' }}>
                          {userInfo.usage.accountsConnected || 0} <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>/ {userInfo.subscription.limits.maxAccounts || '∞'}</span>
                        </div>
                      </div>
                      <div style={{
                        padding: '20px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                          Optimizations
                        </div>
                        <div style={{ fontSize: '1.8rem', fontWeight: '700', color: 'white' }}>
                          {userInfo.usage.optimizations || 0} <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }}>/ {userInfo.subscription.limits.maxOptimizations || '∞'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Method Info */}
                {!isPartner && (
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: 'rgba(255,255,255,0.9)' }}>
                    Payment Method
                  </h3>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'center'
                  }}>
                    <CreditCard size={32} style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }} />
                    <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', marginBottom: '12px' }}>
                      Manage your payment methods via Stripe
                    </p>
                    <button
                      onClick={handleBillingPortal}
                      style={{
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      Open Billing Portal <ExternalLink size={16} />
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}
          
          {/* SECURITY TAB */}
          {activeTab === 'security' && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Shield size={24} />
                  Security Settings
                </h2>

                {/* Password */}
                <div style={{
                  padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Lock size={18} />
                        Password
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Change your password or set up password recovery
                      </div>
                    </div>
                    <a
                      href="https://dashboard.clerk.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      Manage <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                {/* Two-Factor Authentication */}
                <div style={{
                  padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Smartphone size={18} />
                        Two-Factor Authentication
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Add an extra layer of security to your account
                      </div>
                    </div>
                    <a
                      href="https://dashboard.clerk.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '10px 20px',
                        background: '#6366f1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      Enable 2FA <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                {/* Active Sessions */}
                <div style={{
                  padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Globe size={18} />
                        Active Sessions
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        Manage devices and locations where you're signed in
                      </div>
                    </div>
                    <a
                      href="https://dashboard.clerk.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      View All <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                {/* Security Info */}
                <div style={{
                  marginTop: '32px',
                  padding: '20px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Check size={18} />
                    Your account is secure
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
                    Authentication and security features are managed through Clerk for maximum protection. All sensitive operations are logged and monitored.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
